import React from 'react';

const ScoringGuide = () => {
  return (
    <div className="max-w-5xl mx-auto bg-white p-8 rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        Activity Scoring Guide
      </h1>
      <p className="text-gray-700 mb-8 leading-relaxed">
        Each EHS activity is scored across three dimensions to calculate the actual workload required. 
        Use this guide to consistently assess activities and ensure accurate FTE calculations.
      </p>

      {/* Complexity % */}
      <section className="mb-10">
        <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-blue-900 mb-2">
            Complexity % (0-100)
          </h2>
          <p className="text-blue-800 font-semibold">
            Measures the technical difficulty, regulatory requirements, and expertise needed
          </p>
        </div>

        <div className="space-y-6">
          {/* Low Complexity */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">Low Complexity (0-25%)</h3>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                Basic
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Simple, routine tasks with clear procedures</li>
                <li>Minimal regulatory requirements or straightforward compliance</li>
                <li>Basic documentation and record-keeping</li>
                <li>Little technical knowledge required</li>
                <li>Standardized processes with checklists</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>Monthly fire extinguisher inspections</li>
                <li>Basic safety training (slips, trips, falls)</li>
                <li>Routine safety committee meetings</li>
                <li>Simple injury/illness record maintenance</li>
                <li>PPE inventory management</li>
              </ul>
            </div>
          </div>

          {/* Medium Complexity */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">Medium Complexity (26-60%)</h3>
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                Moderate
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Moderate technical expertise required</li>
                <li>Multiple regulatory requirements to coordinate</li>
                <li>Requires interpretation of regulations or standards</li>
                <li>Specialized training or certification needed</li>
                <li>Cross-functional coordination required</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>Hazard communication program management</li>
                <li>Industrial hygiene sampling and analysis</li>
                <li>Environmental permit compliance and reporting</li>
                <li>Contractor safety orientation programs</li>
                <li>Emergency response planning</li>
                <li>OSHA recordkeeping and reporting</li>
              </ul>
            </div>
          </div>

          {/* High Complexity */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">High Complexity (61-100%)</h3>
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                Advanced
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Highly technical, specialized knowledge essential</li>
                <li>Complex regulatory framework with multiple agencies</li>
                <li>Advanced certifications or degrees required</li>
                <li>Requires engineering controls or technical analysis</li>
                <li>High-stakes decision making with significant consequences</li>
                <li>Multiple stakeholder coordination and complex approvals</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>Process Safety Management (PSM) program</li>
                <li>Radiation safety program management</li>
                <li>Biological safety level 3 (BSL-3) lab operations</li>
                <li>Complex air quality permitting and modeling</li>
                <li>RCRA hazardous waste program management</li>
                <li>Industrial ventilation system design</li>
                <li>Asbestos or lead abatement oversight</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Risk % */}
      <section className="mb-10">
        <div className="bg-orange-50 border-l-4 border-orange-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-orange-900 mb-2">
            Risk % (0-100)
          </h2>
          <p className="text-orange-800 font-semibold">
            Measures the potential severity of consequences from non-compliance or incidents
          </p>
        </div>

        <div className="space-y-6">
          {/* Low Risk */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-orange-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">Low Risk (0-25%)</h3>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                Minimal Impact
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Minor injuries possible (first aid only)</li>
                <li>Limited environmental impact potential</li>
                <li>Low regulatory penalties for non-compliance</li>
                <li>Minimal operational disruption if incident occurs</li>
                <li>No potential for fatalities or serious injuries</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>Office ergonomics programs</li>
                <li>General housekeeping inspections</li>
                <li>Employee wellness initiatives</li>
                <li>Non-hazardous waste recycling</li>
                <li>Parking lot safety</li>
              </ul>
            </div>
          </div>

          {/* Medium Risk */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-orange-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">Medium Risk (26-60%)</h3>
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                Moderate Impact
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Potential for recordable injuries or illnesses</li>
                <li>Moderate environmental impacts possible</li>
                <li>Regulatory violations could result in citations/fines</li>
                <li>Temporary operational disruptions possible</li>
                <li>Workers' compensation claims likely</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>Forklift safety program</li>
                <li>Machine guarding compliance</li>
                <li>Chemical storage and handling</li>
                <li>Storm water pollution prevention</li>
                <li>Lockout/tagout (LOTO) program</li>
                <li>Confined space entry program</li>
              </ul>
            </div>
          </div>

          {/* High Risk */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-orange-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">High Risk (61-100%)</h3>
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                Critical Impact
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Potential for fatalities or permanent disabilities</li>
                <li>Major environmental releases or contamination</li>
                <li>Catastrophic incidents possible (explosion, fire, collapse)</li>
                <li>Significant regulatory penalties (6+ figures)</li>
                <li>Long-term operational shutdown risk</li>
                <li>Potential for community evacuation or public health emergency</li>
                <li>Criminal liability possible</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>High-hazard chemical processes (PSM/RMP)</li>
                <li>High-voltage electrical safety programs</li>
                <li>Excavation and trenching safety</li>
                <li>Fall protection for heights &gt;20 feet</li>
                <li>Infectious disease control (Ebola, COVID-19)</li>
                <li>Radioactive materials management</li>
                <li>Explosive materials handling</li>
                <li>Crane and heavy lift operations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Environment % */}
      <section className="mb-10">
        <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-green-900 mb-2">
            Environment % (0-100)
          </h2>
          <p className="text-green-800 font-semibold">
            Measures the environmental compliance burden and ecological impact potential
          </p>
        </div>

        <div className="space-y-6">
          {/* Low Environment */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-green-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">Low Environment (0-25%)</h3>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                Minimal Environmental
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Little to no environmental regulatory requirements</li>
                <li>Minimal potential for environmental releases</li>
                <li>No permits or reporting required</li>
                <li>Low ecological impact</li>
                <li>Standard waste management only</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>General safety training programs</li>
                <li>Ergonomics assessments</li>
                <li>Workplace violence prevention</li>
                <li>Hearing conservation (non-industrial)</li>
                <li>Behavioral safety observations</li>
              </ul>
            </div>
          </div>

          {/* Medium Environment */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-green-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">Medium Environment (26-60%)</h3>
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                Moderate Environmental
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Some environmental permits or reporting required</li>
                <li>Moderate potential for air, water, or soil impacts</li>
                <li>Regulated waste streams present</li>
                <li>Regular environmental monitoring needed</li>
                <li>EPA or state DEP oversight possible</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>Storm water management programs</li>
                <li>Universal waste handling (batteries, lamps, electronics)</li>
                <li>Small quantity generator hazardous waste</li>
                <li>Wastewater discharge permits (non-industrial)</li>
                <li>Spill prevention control and countermeasure (SPCC) plans</li>
                <li>Refrigerant management (CFC/HCFC phase-out)</li>
              </ul>
            </div>
          </div>

          {/* High Environment */}
          <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-green-300 transition">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">High Environment (61-100%)</h3>
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                Significant Environmental
              </span>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">Characteristics:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Multiple complex environmental permits required</li>
                <li>Potential for major environmental releases or contamination</li>
                <li>RCRA large quantity generator status</li>
                <li>Title V air quality permits or PSD review</li>
                <li>Extensive monitoring, testing, and reporting</li>
                <li>Potential for Superfund/CERCLA liability</li>
                <li>Community right-to-know reporting (EPCRA)</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4">Examples:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li>Major air emissions sources (Title V permitting)</li>
                <li>Large-scale hazardous waste generation and treatment</li>
                <li>Industrial wastewater treatment systems</li>
                <li>Remediation of contaminated sites</li>
                <li>PCB transformer management and disposal</li>
                <li>Medical/infectious waste incinerators</li>
                <li>Underground/aboveground storage tank management</li>
                <li>Hazardous chemical storage (RMP threshold quantities)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Scoring Tips */}
      <section className="mb-8">
        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-purple-900 mb-4 flex items-center">
            <span className="text-3xl mr-3">💡</span>
            Scoring Best Practices
          </h2>
          <div className="space-y-4 text-gray-700">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">1. Be Consistent</h3>
              <p>Score similar activities the same way. Use one person or team to score all activities for consistency.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">2. Consider Your Organization</h3>
              <p>Scores should reflect your specific facility, not generic industry standards. A chemical storage program at a university lab differs from one at a refinery.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">3. Look at Historical Data</h3>
              <p>Review past incidents, near-misses, citations, and workers' comp claims to inform risk scoring.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">4. Document Your Rationale</h3>
              <p>Keep notes on why you assigned specific scores. This supports your FTE justification and ensures repeatability.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">5. Seek Input</h3>
              <p>Consult with operations managers, maintenance supervisors, and frontline staff who understand the actual work involved.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">6. Review Annually</h3>
              <p>Scores can change as regulations evolve, processes change, or organizational priorities shift.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Reference Table */}
      <section className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Reference Summary</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-2 border-gray-300">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="border border-gray-300 p-3 text-left">Score Range</th>
                <th className="border border-gray-300 p-3 text-left">Complexity</th>
                <th className="border border-gray-300 p-3 text-left">Risk</th>
                <th className="border border-gray-300 p-3 text-left">Environment</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              <tr className="bg-green-50">
                <td className="border border-gray-300 p-3 font-semibold">0-25%</td>
                <td className="border border-gray-300 p-3">Simple, routine tasks with clear procedures</td>
                <td className="border border-gray-300 p-3">Minor injuries only, minimal operational impact</td>
                <td className="border border-gray-300 p-3">Little/no environmental requirements</td>
              </tr>
              <tr className="bg-yellow-50">
                <td className="border border-gray-300 p-3 font-semibold">26-60%</td>
                <td className="border border-gray-300 p-3">Moderate expertise, specialized training needed</td>
                <td className="border border-gray-300 p-3">Recordable injuries possible, regulatory violations</td>
                <td className="border border-gray-300 p-3">Some permits/reporting, regulated waste streams</td>
              </tr>
              <tr className="bg-red-50">
                <td className="border border-gray-300 p-3 font-semibold">61-100%</td>
                <td className="border border-gray-300 p-3">Highly technical, advanced certifications required</td>
                <td className="border border-gray-300 p-3">Fatality potential, catastrophic incidents possible</td>
                <td className="border border-gray-300 p-3">Multiple complex permits, major release potential</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Formula Impact */}
      <section className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
        <h2 className="text-xl font-bold text-blue-900 mb-3">How Scores Affect FTE Calculations</h2>
        <div className="text-gray-700 space-y-3">
          <p>
            The three percentages are <strong>additive</strong>, meaning they combine to increase the base hours for each activity:
          </p>
          <div className="bg-white p-4 rounded border border-blue-200 font-mono text-sm">
            Weighted Hours = Base Hours × (1 + Complexity% + Risk% + Environment%)
          </div>
          <p className="mt-3"><strong>Example:</strong></p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Activity: Hazardous Waste Management</li>
            <li>Base Hours: 200</li>
            <li>Complexity: 50% | Risk: 40% | Environment: 70%</li>
            <li>Calculation: 200 × (1 + 0.50 + 0.40 + 0.70) = 200 × 2.60 = <strong>520 hours</strong></li>
          </ul>
          <p className="mt-3 font-semibold text-blue-900">
            Higher scores = More hours = More FTE required. Score honestly to ensure adequate staffing!
          </p>
        </div>
      </section>
    </div>
  );
};

export default ScoringGuide;
